/**
 * Pemisah panel yang bisa diseret.
 *
 * Lebar rel kanan sebelumnya adalah angka tetap 288 piksel. Angka itu cukup
 * untuk sederet parameter, dan menjadi sempit begitu rel yang sama harus
 * memuat penyunting susunan track — dua kotak angka berdampingan, sebuah
 * daftar kurva, dan pilihan sumbu — sementara di tab sebelahnya 288 piksel
 * masih terlalu lebar. Satu angka tidak bisa benar untuk keduanya, jadi yang
 * disediakan bukan angka lain melainkan sebuah pegangan.
 *
 * Lebarnya disimpan oleh pemanggil, biasanya lewat `useStored`, sebab rel yang
 * kembali ke lebar bawaan setiap kali layar dibuka memaksa penggunanya
 * menyeret ulang hal yang sama setiap pagi.
 */
import { useCallback, useRef, useState } from "react";

const KEYBOARD_STEP = 16;

export function Resizer({
  width,
  onChange,
  min = 240,
  max = 640,
  /** `"right"` untuk rel di tepi kanan: menyeret ke kiri melebarkannya. */
  side = "right",
  label = "Resize panel",
}: {
  width: number;
  onChange: (width: number) => void;
  min?: number;
  max?: number;
  side?: "left" | "right";
  label?: string;
}) {
  const [active, setActive] = useState(false);
  const [hover, setHover] = useState(false);
  const drag = useRef<{ x: number; width: number } | null>(null);

  const clamp = useCallback(
    (value: number) => Math.min(max, Math.max(min, Math.round(value))),
    [min, max],
  );

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={width}
      aria-valuemin={min}
      aria-valuemax={max}
      tabIndex={0}
      onPointerDown={(event) => {
        // Hanya tombol kiri: klik kanan di perbatasan panel adalah permintaan
        // menu konteks, bukan permintaan menyeret.
        if (event.button !== 0) return;
        drag.current = { x: event.clientX, width };
        setActive(true);
        event.currentTarget.setPointerCapture(event.pointerId);
      }}
      onPointerMove={(event) => {
        if (!drag.current) return;
        const delta = event.clientX - drag.current.x;
        onChange(clamp(drag.current.width + (side === "right" ? -delta : delta)));
      }}
      onPointerUp={(event) => {
        if (!drag.current) return;
        drag.current = null;
        setActive(false);
        event.currentTarget.releasePointerCapture(event.pointerId);
      }}
      onPointerCancel={() => {
        drag.current = null;
        setActive(false);
      }}
      onDoubleClick={() => onChange(clamp(0))}
      onKeyDown={(event) => {
        // Panah kiri melebarkan rel kanan, sebagaimana menyeret ke kiri.
        const direction =
          event.key === "ArrowLeft" ? -1 : event.key === "ArrowRight" ? 1 : 0;
        if (!direction) return;
        event.preventDefault();
        onChange(clamp(width + (side === "right" ? -direction : direction) * KEYBOARD_STEP));
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title={`${label} — drag; double-click for the narrowest width`}
      style={{
        width: 5,
        flex: "none",
        alignSelf: "stretch",
        cursor: "col-resize",
        // Garisnya sendiri tipis; yang lebar adalah sasaran tangkapnya. Tepi
        // panel setebal satu piksel bisa dilihat, tetapi tidak bisa diraih.
        background: active || hover ? "var(--accent)" : "var(--line)",
        opacity: active ? 1 : hover ? 0.6 : 1,
        transition: "background 120ms",
        touchAction: "none",
      }}
    />
  );
}
